import { formatPhone, NavIcon, type SearchGroup, type SearchResult } from "@majitap/ui";
import { useQueries } from "@tanstack/react-query";
import {
  Building2,
  ClockAlert,
  Droplet,
  FilePlus2,
  Handshake,
  type LucideIcon,
  Package,
  PackageCheck,
  PackagePlus,
  ReceiptText,
  Truck,
  UserRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, errorOf } from "./api";
import { deliveryHref } from "./deliveries";
import { messageFor } from "./error-copy";
import { APPROVAL_STATUS, DELIVERY_STATUS, ORDER_STATUS } from "./labels";
import { allowed, type ConsoleSection, type Gate } from "./nav";

const icon = (Icon: LucideIcon) => <Icon size={16} aria-hidden="true" />;

/** How many results each group shows. */
const LIMIT = 5;
/** Typing pauses this long before a search goes out: one request per group, not per keystroke. */
export const DEBOUNCE_MS = 250;
/** Shorter queries match too much to be useful. */
export const MIN_QUERY = 2;

interface Source {
  id: string;
  label: string;
  /** The same gate as the list it searches (src/nav.ts). */
  gate: Gate;
  search: (q: string) => Promise<SearchResult[]>;
  /** Where a result opens: its id, or a path when the result's id is one (deliveries). */
  href: (id: string) => string;
}

const join = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" · ");

/** The list endpoints that take `q` (the shell spec, P8-57): orders, deliveries, people and trucks. */
export const SOURCES: Source[] = [
  {
    id: "orders",
    label: "Orders",
    gate: "orders.read",
    href: (id) => `/orders/${id}`,
    search: async (q) => {
      const { data, error } = await api.GET("/v1/admin/orders", { params: { query: { q, limit: LIMIT } } });
      if (error || !data) throw errorOf(error);
      return data.data.map((o) => ({
        id: o.id,
        label: o.order_number,
        description: join(o.business?.name ?? o.customer.name, ORDER_STATUS[o.status][0]),
        icon: icon(Package),
      }));
    },
  },
  {
    id: "deliveries",
    label: "Deliveries",
    gate: "orders.read",
    // the delivery's page sits under its order: the result's id is the path
    href: (path) => path,
    search: async (q) => {
      const { data, error } = await api.GET("/v1/admin/dispatch/deliveries", {
        params: { query: { q, limit: LIMIT, sort: "newest" } },
      });
      if (error || !data) throw errorOf(error);
      return data.data.map((d) => ({
        id: deliveryHref(d.order.id, d.id),
        label: d.delivery_number,
        description: join(d.business?.name ?? d.customer.name, d.truck?.plate_number, DELIVERY_STATUS[d.status][0]),
        icon: icon(PackageCheck),
      }));
    },
  },
  {
    id: "customers",
    label: "Customers",
    gate: "anyone",
    href: (id) => `/customers/${id}`,
    search: async (q) => {
      const { data, error } = await api.GET("/v1/admin/customers", { params: { query: { q, limit: LIMIT } } });
      if (error || !data) throw errorOf(error);
      return data.data.map((c) => ({
        id: c.id,
        label: c.user.name,
        description: join(formatPhone(c.user.phone_number), c.user.email),
        icon: icon(UserRound),
      }));
    },
  },
  {
    id: "businesses",
    label: "Businesses",
    gate: "anyone",
    href: (id) => `/businesses/${id}`,
    search: async (q) => {
      const { data, error } = await api.GET("/v1/admin/businesses", { params: { query: { q, limit: LIMIT } } });
      if (error || !data) throw errorOf(error);
      return data.data.map((b) => ({
        id: b.id,
        label: b.business_name,
        description: join(formatPhone(b.phone), b.email),
        icon: icon(Building2),
      }));
    },
  },
  {
    id: "partners",
    label: "Partners",
    gate: "partners.manage",
    href: (id) => `/partners/${id}`,
    search: async (q) => {
      const { data, error } = await api.GET("/v1/admin/partners", { params: { query: { q, limit: LIMIT } } });
      if (error || !data) throw errorOf(error);
      return data.data.map((p) => ({
        id: p.id,
        label: p.name,
        description: join(
          formatPhone(p.phone),
          p.approval_status === "pending" ? "Pending approval" : APPROVAL_STATUS[p.approval_status][0],
        ),
        icon: icon(Handshake),
      }));
    },
  },
  {
    id: "trucks",
    label: "Trucks",
    gate: "anyone",
    href: (id) => `/fleet?truck=${id}`,
    search: async (q) => {
      const { data, error } = await api.GET("/v1/admin/fleet/trucks", { params: { query: { q, limit: LIMIT } } });
      if (error || !data) throw errorOf(error);
      return data.data.map((t) => ({
        id: t.id,
        label: t.plate_number,
        description: join(
          t.fleet_number === null ? null : `Fleet no. ${t.fleet_number}`,
          [t.make, t.model].filter(Boolean).join(" "),
        ),
        icon: icon(Truck),
      }));
    },
  },
];

export const sourcesFor = (permissions: readonly string[]) => SOURCES.filter((s) => allowed(s.gate, permissions));

/** Every page the role's menu has, for the palette to filter: "disp" → Dispatch. */
export function pageGroup(menu: ConsoleSection[]): SearchGroup {
  return {
    id: "pages",
    label: "Pages",
    local: true,
    results: menu.flatMap((s) =>
      s.items.map((i) => {
        return {
          id: i.path,
          label: i.label,
          description: i.description,
          icon: <NavIcon icon={i.icon} size={16} />,
          keywords: s.label ? [s.label] : undefined,
        };
      }),
    ),
  };
}

interface Action {
  id: string;
  label: string;
  description: string;
  gate: Gate;
  icon: LucideIcon;
  keywords?: string[];
}

/** What staff most often go to do; the id is where it opens. */
const ACTIONS: Action[] = [
  {
    id: "/orders/new",
    label: "New order",
    description: "For a customer or a business",
    gate: "orders.write",
    icon: PackagePlus,
    keywords: ["place", "create"],
  },
  {
    id: "/invoices/new",
    label: "New invoice",
    description: "Bill a customer or a business",
    gate: "invoices.issue",
    icon: FilePlus2,
    keywords: ["bill", "create"],
  },
  {
    id: "/deliveries?late=true",
    label: "Late deliveries",
    description: "Not on the road yet, past their time",
    gate: "orders.read",
    icon: ClockAlert,
  },
  {
    id: "/payments?view=till",
    label: "Till payments to match",
    description: "Money in with no order or invoice",
    gate: "payments.record",
    icon: ReceiptText,
    keywords: ["mpesa", "match"],
  },
  {
    id: "/sensors?show=low",
    label: "Tanks running low",
    description: "Below their alert level",
    gate: "customers.manage",
    icon: Droplet,
    keywords: ["sensor", "level"],
  },
];

/** The actions the role may take, for the palette to filter. */
export function actionGroup(permissions: readonly string[]): SearchGroup {
  return {
    id: "actions",
    label: "Actions",
    local: true,
    results: ACTIONS.filter((a) => allowed(a.gate, permissions)).map((a) => ({
      id: a.id,
      label: a.label,
      description: a.description,
      icon: icon(a.icon),
      keywords: a.keywords,
    })),
  };
}

export function useDebounced<T>(value: T, ms = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Search every source the role may see for `query`, as palette groups, each loading until it answers. */
export function useRecordSearch(query: string, permissions: readonly string[]): { groups: SearchGroup[] } {
  const typed = query.trim();
  const q = useDebounced(typed);
  const sources = sourcesFor(permissions);
  const results = useQueries({
    queries: sources.map((source) => ({
      queryKey: ["search", source.id, q],
      queryFn: () => source.search(q),
      enabled: q.length >= MIN_QUERY,
      staleTime: 30_000,
    })),
  });
  if (typed.length < MIN_QUERY) return { groups: [] };
  const groups = sources.map((source, i) => ({
    id: source.id,
    label: source.label,
    results: q.length < MIN_QUERY ? [] : (results[i].data ?? []),
    // still typing counts as on its way, so the list doesn't say "no match" too early
    loading: results[i].isPending || q !== typed,
    error: results[i].isError ? messageFor(errorOf(results[i].error)) : undefined,
  }));
  return { groups };
}
